<template>
  <div>
    <div
      v-if="isLoading"
      class="w-full px-2 sm:px-4 lg:px-6 pt-4 space-y-4"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="bg-white rounded-lg p-4 mx-4 border border-gray-200"
      >
        <div class="flex justify-between gap-3">
          <div class="flex-1 space-y-2">
            <div class="flex items-center justify-between">
              <Skeleton
                width="4rem"
                height="2rem"
                animated
              />
              <div class="flex gap-1">
                <Skeleton
                  width="2rem"
                  height="2rem"
                  animated
                />
                <Skeleton
                  width="2rem"
                  height="2rem"
                  animated
                />
              </div>
            </div>
            <br>
            <Skeleton
              width="100%"
              height="1.5rem"
              animated
            />
            <Skeleton
              width="80%"
              height="1.5rem"
              animated
            />
            <br>
            <hr>
            <br>
            <div class="flex gap-2">
              <Skeleton
                width="6rem"
                height="1.5rem"
                animated
              />
              <Skeleton
                width="6rem"
                height="1.5rem"
                animated
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="!isLoading && isErrored"
      class="w-full px-2 sm:px-4 lg:px-6 py-2 text-center"
    >
      <div class="bg-red-100 text-red-700 py-1 px-2 rounded-md flex items-center gap-2 justify-center">
        <i class="pi pi-exclamation-circle" />
        <p>
          Failed to load activity
        </p>
      </div>
    </div>

    <ActivityFeedTimeline
      v-else
      :timeline="timeline"
      :pinned-notes="pinnedNotes"
      :is-loading="isLoading"
      :showActions="false"
      :is-fetching-more="isFetchingMore"
      :has-more="hasMore"
      :users="users"
      @load-more="loadMore()"
      @refresh="handleFetch()"
    />
  </div>
</template>

<script lang="ts" setup>
import type { NoteType, TimelineType, User } from '@prisma/client'

const route = useRoute()
const toast = useToast()

const isLoading = ref<boolean>(true)
const isErrored = ref<boolean>(false)

// Pinned Notes
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pinnedNotes = ref<any[]>([])

// Timeline
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const timeline = ref<any[]>([])
const timelineCount = ref<number>()
const skip = ref(0)
const take = 10
const hasMore = ref(true)
const isFetchingMore = ref(false)
const { getTimelineNotesForDashboard } = useTimeline()

// Options
const options = computed(() => {
  let startDate: Date | undefined
  let endDate: Date | undefined
  if (selectedInterval.value) {
    if (selectedInterval.value[0] !== null) {
      startDate = new Date(selectedInterval.value[0])
      startDate.setHours(0o0, 0o0, 0o0)
    }
    if (selectedInterval.value[1] !== null) {
      endDate = new Date(selectedInterval.value[1])
      endDate.setHours(23, 59, 59)
    }
  }

  return {
    categories: selectedCategories.value as TimelineType[],
    businesses: selectedBusinesses.value,
    contacts: selectedContacts.value,
    startedAt: startDate,
    endedAt: endDate,
    noteTypes: selectedNoteTypes.value,
    noteAuthors: selectedNoteAuthors.value,
    skip: skip.value,
    take: take,
  }
})

const handleFetch = async () => {
  skip.value = 0
  await fetchTimelineNotes()
}

// Fetch Timeline Notes
const fetchTimelineNotes = async () => {
  isLoading.value = true
  hasMore.value = true

  const dashboardOptions = {
    ...options.value,
  }

  try {
    const response = await getTimelineNotesForDashboard(dashboardOptions)
    const data = response?.data || []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timeline.value = data as unknown as any[]
    timelineCount.value = data.length

    if (data.length < take) {
      hasMore.value = false
    }

    skip.value += take
  }
  catch (error) {
    isErrored.value = true
    isLoading.value = false
    console.error('Error fetching notes:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch notes',
      life: 3000,
    })
  }
  finally {
    isLoading.value = false
    sidebarVisible.value = false
  }
}

// Load More
const loadMore = async () => {
  if (!hasMore.value) {
    return
  }

  isFetchingMore.value = true

  try {
    const dashboardOptions = {
      ...options.value,
    }
    const response = await getTimelineNotesForDashboard(dashboardOptions)

    if (!response || !response.data) {
      isErrored.value = true
      isLoading.value = false
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch notes',
        life: 3000,
      })
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newData = response.data as unknown as any[]

    skip.value += take

    timeline.value.push(...newData)
  }
  catch (error) {
    isErrored.value = true
    isLoading.value = false
    console.error('Failed to load more records.', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load more records.',
      life: 3000,
    })
  }
  finally {
    isFetchingMore.value = false
  }
}

// Users
const { getUsers } = useUsers()
const users = ref<User[]>([])

// Tabs
const selectedTab = ref()

// Filters Sidebar
const sidebarVisible = ref(false)

// // By Date
const selectedInterval = ref()

// // By Business
const selectedBusinesses = ref<string[]>([])

// // By Contacts
const selectedContacts = ref<string[]>([])

// // By Category
const selectedCategories = ref<TimelineType[]>(['Notes'])

// // Notes
const selectedNoteTypes = ref<NoteType[]>(['Note', 'Call', 'Email', 'SMS', 'Inspection', 'Offer', 'Meeting'])
const selectedNoteAuthors = ref<string[]>([])

onMounted(async () => {
  const tabValue = route.query?.tab
  selectedTab.value = tabValue || '0'

  const resUsers = await getUsers()
  users.value = resUsers.data

  await handleFetch()
})

defineExpose({ handleFetch })
</script>
